import type {
  InterestClassBookSetting,
  RealEstateLease,
  ShortSmallLeaseExclReg,
  TreatmentFixedAssetControl,
} from '@libs/enums';
import type { ItemAttributes, ItemNames, OrEmpty, WithExclusionObject } from '@ps-smart-cp/ppfw-client';
import {
  Bool,
  Characters,
  ItemName,
  Locale,
  SingleValue,
  ValueSet,
  WithExclusion,
} from '@ps-smart-cp/ppfw-client';

interface Schema {
  /**
   * 分類(表示用)
   */
  subCategory: string;
  /**
   * 分類コード
   */
  subCategoryCode: string;
  /**
   * 分類名称
   */
  subCategoryName: string;
  /**
   * 短期/少額リースの除外規定
   */
  isShortSmallLeaseExclReg: boolean;
  /**
   * 短期/少額リースの除外規定(抽出条件)
   */
  shortSmallLeaseExclRegCondition: Set<ShortSmallLeaseExclReg>;
  /**
   * 分類範囲
   */
  subCategoryCodeRange: WithExclusionObject;
  /**
   * リース対象区分
   */
  isLeaseTarget: boolean;
  /**
   * 固定資産管理上での扱い
   */
  treatmentFixedAssetControl: OrEmpty<TreatmentFixedAssetControl>;
  /**
   * 不動産リース
   */
  isRealEstateLease: boolean;
  /**
   * 不動産リース(抽出条件)
   */
  realEstateLeaseCondition: Set<RealEstateLease>;
  /**
   * 利息区分
   */
  interestClass: OrEmpty<InterestClassBookSetting>;
}

interface Record {
  subCategory: string;
  subCategoryCode: string;
  subCategoryName: string;
  isShortSmallLeaseExclReg: boolean;
  shortSmallLeaseExclRegCondition: string[];
  subCategoryRange: string;
  isSubCategoryRangeExclusion: boolean;
  interestClass: string;
  isRealEstateLease: boolean;
  treatmentFixedAssetControl: string;
  isLeaseTarget: boolean;
  realEstateLeaseCondition: string[];
}

const attributes = {
  subCategory: Characters.preset.alphanumericals({
    minLen: 6,
    maxLen: 6,
  }),
  subCategoryCode: Characters.preset.alphanumericals({
    minLen: 6,
    maxLen: 6,
    required: true,
  }),
  subCategoryName: Characters.preset.freeFormat(),
  isShortSmallLeaseExclReg: Bool.new(),
  shortSmallLeaseExclRegCondition: ValueSet.new(),
  subCategoryCodeRange: WithExclusion.of(Characters.preset.alphanumericalsRange({ maxLen: 250 })),
  interestClass: SingleValue.shortValue(true),
  isRealEstateLease: Bool.new(),
  treatmentFixedAssetControl: SingleValue.shortValue(true),
  isLeaseTarget: Bool.new(),
  realEstateLeaseCondition: ValueSet.new(),
} satisfies ItemAttributes<Schema>;

const names = {
  subCategory: ItemName.of([Locale.ja, '分類'], [Locale.en, 'Sub Category']),
  subCategoryCode: ItemName.of([Locale.ja, '分類コード']),
  subCategoryName: ItemName.of([Locale.ja, '分類名称'], [Locale.en, 'Sub Category Name']),
  isShortSmallLeaseExclReg: ItemName.of([Locale.ja, '短期/少額リースの除外規定']),
  shortSmallLeaseExclRegCondition: ItemName.of([Locale.ja, '短期/少額リースの除外規定']),
  subCategoryCodeRange: ItemName.of([Locale.ja, '分類範囲']),
  interestClass: ItemName.of([Locale.ja, '利息区分']),
  isRealEstateLease: ItemName.of([Locale.ja, '不動産リース']),
  realEstateLeaseCondition: ItemName.of([Locale.ja, '不動産リース']),
  isLeaseTarget: ItemName.of([Locale.ja, 'リース対象区分']),
  treatmentFixedAssetControl: ItemName.of([Locale.ja, '固定資産管理上での扱い']),
} satisfies ItemNames<Schema>;

const labelNames = {
  leaseTarget: ItemName.of([Locale.ja, 'リース対象']),
};

const subCategoryMst = {
  names,
  labelNames,
  attributes,
};

interface SubCategoryMst {
  schema: Schema;
  record: Record;
}

export { subCategoryMst };
export type { SubCategoryMst };
